import { normalizeToolName } from './tool-config.helpers';

export type TracePhase = 'attack' | 'post-attack' | 'ai-discovery' | 'co-pilot';

const BASE_HIDDEN_TOOL_NAMES = [
  'intelligence',
  'sub-agent-intelligence',
  'sub_agent_intelligence',
  'operator-sub-agent',
  'operator_sub_agent',
  'websearch-tool',
  'ls',
  'long-term-memory-tool',
  'long_term_memory_tool',
] as const;

const ATTACK_ONLY_HIDDEN_TOOL_NAMES = [
  'read-file',
  'read_file',
  'grep',
  'list-files',
  'list_files',
  'end-test',
  'end_test',
] as const;

const WRITE_FILE_TOOL_NAMES = ['write-file', 'write_file'] as const;

const POST_ATTACK_ONLY_HIDDEN_TOOL_NAMES = [
  'todo-write',
  'todo_write',
  ...WRITE_FILE_TOOL_NAMES,
] as const;

const ATTACK_HIDDEN_TOOL_NAMES = new Set([
  ...BASE_HIDDEN_TOOL_NAMES,
  ...ATTACK_ONLY_HIDDEN_TOOL_NAMES,
]);

const POST_ATTACK_HIDDEN_TOOL_NAMES = new Set([
  ...BASE_HIDDEN_TOOL_NAMES,
  ...POST_ATTACK_ONLY_HIDDEN_TOOL_NAMES,
]);

const AI_DISCOVERY_HIDDEN_TOOL_NAMES = new Set<string>();

const COPILOT_SUB_AGENT_SPAWN_TOOL_NAMES = [
  'task',
  'operator',
  'recon',
  'validator',
  'object-creator',
  'background-attack',
  'code-analyst',
  'application-data-analyst',
  'skill-builder',
  'general',
] as const;

const COPILOT_HIDDEN_TOOL_NAMES = new Set([
  ...BASE_HIDDEN_TOOL_NAMES,
  ...COPILOT_SUB_AGENT_SPAWN_TOOL_NAMES,
  ...WRITE_FILE_TOOL_NAMES,
  'grep',
  'list-files',
  'list_files',
]);

const HIDDEN_BY_PHASE: Record<TracePhase, ReadonlySet<string>> = {
  attack: ATTACK_HIDDEN_TOOL_NAMES,
  'post-attack': POST_ATTACK_HIDDEN_TOOL_NAMES,
  'ai-discovery': AI_DISCOVERY_HIDDEN_TOOL_NAMES,
  'co-pilot': COPILOT_HIDDEN_TOOL_NAMES,
};

const HTTP_STANDARD_TOOL_NAMES = new Set([
  'http-tool',
  'dynamic-http-tool',
  'validated-http-tool',
  'http-executor',
]);

function isCoPilotSubAgentToolName(normalized: string): boolean {
  return (
    normalized.startsWith('sub-agent-') || normalized.startsWith('sub_agent_')
  );
}

export function isHiddenToolName(
  toolName: string,
  phase: TracePhase = 'attack',
): boolean {
  const normalized = normalizeToolName(toolName);

  if (phase === 'co-pilot' && isCoPilotSubAgentToolName(normalized)) {
    return true;
  }

  const hiddenNames = HIDDEN_BY_PHASE[phase];

  return hiddenNames.has(normalized) || hiddenNames.has(toolName.toLowerCase());
}

export function usesReadToolCard(toolName: string, phase: TracePhase): boolean {
  const normalized = normalizeToolName(toolName);

  if (normalized !== 'read-file') {
    return false;
  }

  return phase === 'post-attack' || phase === 'co-pilot';
}

export function usesStandardToolCard(
  toolName: string,
  phase: TracePhase,
): boolean {
  const normalized = normalizeToolName(toolName);

  if (phase === 'post-attack') {
    return normalized === 'grep' || normalized === 'list-files';
  }

  if (phase === 'co-pilot') {
    return HTTP_STANDARD_TOOL_NAMES.has(normalized);
  }

  return false;
}
